import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from './Header';
import Comments from '../components/Comments';
import Modal from '../components/Modal';
import Seo from '../components/Seo';
import api from '../api/client';
import './PictureDetail.css';

const CATEGORIES = [
    'categories.painting',
    'categories.digital',
    'categories.photography',
    'categories.sculpture',
    'categories.drawing',
    'categories.mixedMedia'
];

export default function PictureDetail({ translations }) {
    const { publicId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [picture, setPicture] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const [liked, setLiked] = useState(false);
    const [likeCount, setLikeCount] = useState(0);
    const [commentCount, setCommentCount] = useState(0);

    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({ description: '', price: '', category: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [inBasket, setInBasket] = useState(false);
    const [basketBusy, setBasketBusy] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setNotFound(false);
        api.getPicture(publicId)
            .then((data) => {
                if (!active) return;
                if (!data || data.publicId === 'not-found') {
                    setNotFound(true);
                    return;
                }
                setPicture(data);
                setLiked(Boolean(data.likedByUser));
                setLikeCount(data.numberOfLikes || 0);
                setCommentCount(data.commentCount || 0);
            })
            .catch(() => { if (active) setNotFound(true); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [publicId]);

    // The backend only returns the `id` field (OwnPictureDto) when the
    // authenticated caller owns the picture, so it is a reliable owner flag.
    const isOwner = Boolean(picture?.id);

    const handleBasket = async () => {
        if (!user || !picture || basketBusy) return;
        setBasketBusy(true);
        const next = !inBasket;
        setInBasket(next);
        try {
            if (next) {
                await api.addToBasket(picture.publicId);
            } else {
                await api.removeFromBasket(picture.publicId);
            }
            window.dispatchEvent(new Event('basket:changed'));
        } catch {
            setInBasket(!next);
        } finally {
            setBasketBusy(false);
        }
    };

    const handleLike = async () => {
        if (!user || !picture) return;

        // Optimistic update: flip immediately, reconcile/revert once the backend answers.
        const prevLiked = liked;
        const prevCount = likeCount;
        const nextLiked = !liked;
        setLiked(nextLiked);
        setLikeCount((c) => Math.max(0, c + (nextLiked ? 1 : -1)));

        try {
            const result = await api.likePicture(picture.publicId);
            setLiked(result.likedByUser);
            setLikeCount(result.numberOfLikes);
        } catch {
            // Revert on failure
            setLiked(prevLiked);
            setLikeCount(prevCount);
        }
    };

    const openEdit = () => {
        setForm({
            description: picture.description || '',
            price: picture.price != null ? String(picture.price) : '',
            category: picture.category || CATEGORIES[0],
        });
        setError('');
        setEditing(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setError('');
        setSaving(true);
        try {
            const payload = {
                description: form.description.trim(),
                category: form.category,
            };
            if (form.price !== '') {
                payload.price = Number(form.price);
            }
            const updated = await api.updatePicture(picture.id, payload);
            setPicture(updated);
            setEditing(false);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        const msg = translations['picture-delete-confirm'] || 'Delete this post? This cannot be undone.';
        if (!window.confirm(msg)) return;
        setDeleting(true);
        try {
            await api.deletePicture(picture.publicId);
            navigate('/profile');
        } catch (err) {
            setError(err.message);
            setDeleting(false);
        }
    };

    const renderBody = () => {
        if (loading) {
            return <div className="spinner" />;
        }
        if (notFound || !picture) {
            return (
                <div className="picture-detail-missing">
                    <p>{translations['picture-not-found'] || 'This artwork is not available.'}</p>
                    <Link to="/" className="btn">{translations['picture-back-home'] || 'Back to home'}</Link>
                </div>
            );
        }

        const owner = picture.owner;
        const imageUrl =
            picture.filesURL?.original ||
            picture.filesURL?.compressed ||
            picture.filesURL?.watermarked ||
            '';
        const categoryKey = picture.category?.split?.('.')[1];
        const categoryLabel = translations.categories?.[categoryKey] || categoryKey || picture.category;

        return (
            <div className="picture-detail">
                <div className="picture-detail-image-wrapper">
                    {imageUrl ? (
                        <img className="picture-detail-image" src={imageUrl} alt={picture.title || 'Artwork'} />
                    ) : (
                        <div className="picture-detail-image placeholder">
                            {translations['picture-no-image'] || 'Image not available'}
                        </div>
                    )}
                </div>

                <div className="picture-detail-info">
                    {isOwner && (
                        <span className="picture-detail-owner-badge">
                            {translations['picture-your-artwork'] || 'Your artwork'}
                        </span>
                    )}

                    <h1 className="picture-detail-title">{picture.title || 'Untitled'}</h1>

                    {owner && (
                        <Link to={`/user/${owner.publicId}`} className="picture-detail-artist">
                            {owner.profilePicture ? (
                                <img src={owner.profilePicture} alt="" className="picture-detail-artist-avatar" />
                            ) : (
                                <span className="picture-detail-artist-avatar fallback">
                                    {(owner.username || '?')[0].toUpperCase()}
                                </span>
                            )}
                            <span>{owner.username || 'Unknown'}</span>
                        </Link>
                    )}

                    {picture.price > 0 && (
                        <p className="picture-detail-price">€{picture.price}</p>
                    )}

                    {categoryLabel && (
                        <p className="picture-detail-category">
                            <span className="label">{translations['picture-category'] || 'Category'}:</span> {categoryLabel}
                        </p>
                    )}

                    {picture.description && (
                        <p className="picture-detail-description">{picture.description}</p>
                    )}

                    <div className="picture-detail-stats">
                        <button
                            type="button"
                            className={`picture-detail-like-btn ${liked ? 'liked' : ''}`}
                            onClick={handleLike}
                            disabled={!user}
                            title={user ? (liked ? 'Unlike' : 'Like') : 'Log in to like'}
                        >
                            <span className="material-symbols-outlined">
                                {liked ? 'favorite' : 'favorite_border'}
                            </span>
                            <span>{likeCount}</span>
                        </button>
                        <span className="picture-detail-comment-count">
                            <span className="material-symbols-outlined">chat_bubble</span>
                            {commentCount}
                        </span>
                    </div>

                    {!isOwner && user && (
                        <button
                            type="button"
                            className={`picture-detail-basket-btn ${inBasket ? 'added' : ''}`}
                            onClick={handleBasket}
                            disabled={basketBusy}
                        >
                            {inBasket
                                ? (translations['picture-in-basket'] || '✓ In basket — remove')
                                : (translations['picture-add-basket'] || 'Add to basket')}
                        </button>
                    )}

                    {isOwner && (
                        <div className="picture-detail-owner-actions">
                            <button type="button" className="btn" onClick={openEdit}>
                                {translations['picture-edit'] || 'Edit'}
                            </button>
                            <button
                                type="button"
                                className="btn delete"
                                onClick={handleDelete}
                                disabled={deleting}
                            >
                                {deleting
                                    ? (translations['picture-deleting'] || 'Deleting...')
                                    : (translations['picture-delete'] || 'Delete')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            {picture && !notFound && (
                <Seo
                    title={picture.title || 'Artwork'}
                    description={
                        picture.description
                            ? `${picture.description.slice(0, 160)}`
                            : `${picture.title || 'Original artwork'}${picture.owner?.username ? ` by ${picture.owner.username}` : ''} on ArtSyde.`
                    }
                    image={picture.filesURL?.watermarked || picture.filesURL?.compressed}
                    type="article"
                    jsonLd={{
                        '@context': 'https://schema.org',
                        '@type': 'VisualArtwork',
                        name: picture.title || 'Untitled',
                        image: picture.filesURL?.watermarked || picture.filesURL?.compressed,
                        description: picture.description || undefined,
                        artform: picture.category || undefined,
                        creator: picture.owner?.username
                            ? { '@type': 'Person', name: picture.owner.username }
                            : undefined,
                        ...(picture.price > 0 && {
                            offers: {
                                '@type': 'Offer',
                                price: picture.price,
                                priceCurrency: 'EUR',
                                availability: 'https://schema.org/InStock',
                            },
                        }),
                    }}
                />
            )}
            <Header translations={translations} />
            <div className="picture-detail-container">
                {renderBody()}
                {!loading && !notFound && picture && (
                    <Comments
                        picturePublicId={picture.publicId}
                        translations={translations}
                        onCountChange={(delta) => setCommentCount((c) => Math.max(0, c + delta))}
                    />
                )}
            </div>

            {editing && (
                <Modal
                    title={translations['picture-edit-title'] || 'Edit artwork'}
                    onClose={() => setEditing(false)}
                >
                    <form onSubmit={handleSave}>
                        <div className="app-field">
                            <label className="app-field-label">
                                {translations['picture-field-description'] || 'Description'}
                            </label>
                            <textarea
                                className="app-textarea"
                                value={form.description}
                                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                                placeholder={translations['create-card-description-placeholder'] || 'Describe your artwork'}
                            />
                        </div>
                        <div className="app-field">
                            <label className="app-field-label">
                                {translations['picture-field-price'] || 'Price (€)'}
                            </label>
                            <input
                                type="text"
                                inputMode="decimal"
                                className="app-input"
                                value={form.price}
                                onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                                placeholder="0"
                            />
                        </div>
                        <div className="app-field">
                            <label className="app-field-label">
                                {translations['picture-field-category'] || 'Category'}
                            </label>
                            <select
                                className="app-select"
                                value={form.category}
                                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                            >
                                {(CATEGORIES.includes(form.category) || !form.category
                                    ? CATEGORIES
                                    : [form.category, ...CATEGORIES]
                                ).map((cat) => {
                                    const key = cat.split('.')[1];
                                    return (
                                        <option key={cat} value={cat}>
                                            {translations.categories?.[key] || key || cat}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                        {error && <p className="app-form-error">{error}</p>}
                        <button type="submit" className="app-modal-btn" disabled={saving}>
                            {saving ? (translations['picture-saving'] || 'Saving...') : (translations['picture-save'] || 'Save')}
                        </button>
                    </form>
                </Modal>
            )}
        </>
    );
}
